<template>
  <!-- 代销协议 - 新增/编辑（样式参考 /orders/PaymentProtocolManage/AddPayment） -->
  <div>
    <a-card style="margin: 20px">
      <template slot="title">
        <span>{{ title }}</span>
        <a-tag v-if="form.status && statusCfg(form.status).show"
               :color="statusCfg(form.status).color" style="margin-left: 12px">
          {{ statusCfg(form.status).label }}
        </a-tag>
      </template>
      <template slot="extra">
        <a-button type="primary" @click="onSave" :loading="saveLoading" v-if="!isView">保存</a-button>
        <a-button @click="onCancel" style="margin-left: 8px">取消</a-button>
      </template>

      <a-spin :spinning="loading">
        <div class="form-box">
          <!-- 待审批:编辑保存即重置审批状态,提示用户 -->
          <a-alert v-if="form.status === '1'"
                   type="warning" show-icon style="margin-bottom: 12px"
                   message="该合同当前待审批,编辑保存后状态会重置为待审批(原审批意见将清空)" />
          <!-- 已拒绝:提示审批备注 -->
          <a-alert v-if="form.status === '3' && form.approveRemark"
                   type="error" show-icon style="margin-bottom: 12px"
                   :message="`该合同已被拒绝,审批意见: ${form.approveRemark}`" />
          <!-- 同步失败:提示失败原因(若有) -->
          <a-alert v-if="form.syncStatus === '2' && form.syncFailMsg"
                   type="warning" show-icon style="margin-bottom: 12px"
                   :message="`该合同同步银行失败: ${form.syncFailMsg}`" />
          <a-form-model ref="ruleForm" :model="form" :rules="rules" labelAlign="left">
            <!-- ==================== 代销方 ==================== -->
            <div class="toptitle">代销方</div>

            <!-- 代销商户(电商) — 子组件包裹,不参与 form-model 校验,由 saveDraft 手动校验 -->
            <a-form-item label="代销商户">
              <MchChooseDialog v-model="form.mchName"
                               @chooseDone="onMchChosen" :isDisabled="isView"></MchChooseDialog>
            </a-form-item>

            <!-- 代销账号(通过 WalletChooseDialog 单独选,与委托方一致) — 不参与 form-model 校验 -->
            <a-form-item label="代销账号">
              <a-row>
                <WalletChooseDialog v-model="form.consignAccountName" :isDisabled="isView"
                                    @chooseDone="(w) => onConsignChosen(w)"></WalletChooseDialog>
                <WalletInfo v-if="form.consignAccountNo" :account-no="form.consignAccountNo"
                            :account-name="form.consignAccountName" :account-class="consignAccountClass" />
              </a-row>
            </a-form-item>

            <!-- 代销户名(选择钱包后自动回填) -->
            <a-form-item label="代销户名">
              <a-input v-model.trim="form.consignAccountName" placeholder="代销方户名(选钱包后自动回填)"
                       :disabled="isView" style="max-width: 300px" />
            </a-form-item>

            <!-- ==================== 委托方 ==================== -->
            <div class="toptitle">委托方</div>

            <!-- 委托钱包(委托方=出资方,通过 WalletChooseDialog 选) — 不参与 form-model 校验 -->
            <a-form-item label="委托账户">
              <a-row>
                <WalletChooseDialog v-model="form.entrustAccountName" :isDisabled="isView"
                                    @chooseDone="onEntrustChosen"></WalletChooseDialog>
                <WalletInfo v-if="form.entrustAccountNo" :account-no="form.entrustAccountNo"
                            :account-name="form.entrustAccountName" :account-class="entrustAccountClass" />
              </a-row>
            </a-form-item>

            <!-- 委托户名(选择钱包后自动回填) -->
            <a-form-item label="委托户名">
              <a-input v-model.trim="form.entrustAccountName" placeholder="委托方户名(选钱包后自动回填)"
                       :disabled="isView" style="max-width: 300px" />
            </a-form-item>

            <!-- ==================== 合同信息 ==================== -->
            <div class="toptitle">合同信息</div>

            <a-form-item label="合同文本编号">
              <a-input v-model.trim="form.textContractNo" placeholder="可选,文本协议编号"
                       :disabled="isView" style="max-width: 300px" />
            </a-form-item>

            <a-form-model-item label="合同起始日期" prop="contractDateStart">
              <a-date-picker v-model="form.contractDateStart" valueFormat="YYYY-MM-DD"
                             :disabled="isView" style="width: 240px" placeholder="请选择起始日期" />
            </a-form-model-item>

            <a-form-model-item label="合同截止日期" prop="contractDateEnd">
              <a-date-picker v-model="form.contractDateEnd" valueFormat="YYYY-MM-DD"
                             :disabled="isView" style="width: 240px" placeholder="请选择截止日期" />
            </a-form-model-item>

            <a-form-item label="年度代销限额">
              <a-input-number v-model="form.consignYearLimit" :min="0" :precision="2"
                              :disabled="isView" style="width: 240px" placeholder="可选" />
            </a-form-item>

            <a-form-item label="备注">
              <a-textarea v-model="form.remark" :rows="2" :disabled="isView"
                          placeholder="可选" style="max-width: 600px" />
            </a-form-item>

            <!-- ==================== 合同明细 ==================== -->
            <div class="toptitle" style="margin-top: 16px">
              合同明细
              <a-button v-if="!isView" type="dashed" icon="plus" size="small" @click="addDetail"
                        style="margin-left: 12px">添加明细</a-button>
            </div>
            <div v-if="!form.detailList || form.detailList.length === 0" class="empty-tip">
              暂无明细,请点击"添加明细"
            </div>
            <a-row :gutter="20">
              <a-col v-for="(d, idx) in form.detailList" :key="idx"
                     :xs="24" :sm="24" :md="12" :lg="12" :xl="8">
                <a-card size="small" class="detail-card" :bordered="true">
                  <template slot="title">
                    <span>明细 {{ idx + 1 }}</span>
                    <a-tag v-if="d.contractType && contractTypeCfg[d.contractType]"
                           :color="contractTypeCfg[d.contractType].color" style="margin-left: 8px">
                      {{ contractTypeCfg[d.contractType].label }}
                    </a-tag>
                  </template>
                  <template slot="extra">
                    <a-button v-if="!isView" type="link" danger size="small" @click="removeDetail(idx)">删除</a-button>
                  </template>

                  <div class="detail-field">
                    <label class="required">协议类型</label>
                    <a-select v-model="d.contractType" placeholder="请选择类型" style="width: 100%"
                              :disabled="isView">
                      <a-select-option v-for="o in contractTypeOptions" :key="o.value" :value="o.value">
                        {{ o.label }}
                      </a-select-option>
                    </a-select>
                  </div>
                  <div class="detail-field">
                    <label class="required">对方账号</label>
                    <WalletInfo v-if="d.oppAccountNo"
                                :account-name="d.oppAccountName"
                                :account-no="d.oppAccountNo"
                                :account-class="d.oppAccountClass"
                                :show-account-name="false"
                                :show-account-no="true" />
                    <span v-else class="placeholder">选对手方钱包后展示</span>
                  </div>
                  <div class="detail-field">
                    <label class="required">对方户名</label>
                    <WalletChooseDialog v-model="d.oppAccountName" :isDisabled="isView"
                                        @chooseDone="(w) => onOppChosen(d, w)"></WalletChooseDialog>
                  </div>
                  <div class="detail-field">
                    <label class="required">服务费规则</label>
                    <a-select v-model="d.feeNo" placeholder="请选择服务费规则"
                              :disabled="isView" style="width: 100%"
                              @change="(v) => onFeeRuleChange(d, v)">
                      <a-select-option v-for="r in feeRuleOptions" :key="r.feeNo" :value="r.feeNo">
                        {{ r.feeName }}
                      </a-select-option>
                    </a-select>
                  </div>
                  <div class="detail-field">
                    <label>手续费备注</label>
                    <a-input v-model.trim="d.feeRemark" placeholder="可选" :disabled="isView" />
                  </div>
                </a-card>
              </a-col>
            </a-row>
          </a-form-model>
        </div>
      </a-spin>
    </a-card>
  </div>
</template>

<script>
import MchChooseDialog from '@/view/components/ChooseDialog/MchChooseDialog.vue'
import WalletChooseDialog from '@/view/components/ChooseDialog/WalletChooseDialog.vue'
import WalletInfo from '@/view/components/WalletInfo.vue'
import { contractStatusConfig, contractTypeConfig, toOptions } from '@/utils/bizDict/consignDict'

export default {
  name: 'ConsignContractAdd',
  components: { MchChooseDialog, WalletChooseDialog, WalletInfo },
  data() {
    return {
      loading: false,
      saveLoading: false,
      title: '代销协议-新增',
      isEdit: false,
      isView: false,
      consignAccountClass: undefined,
      entrustAccountClass: undefined,
      contractTypeOptions: toOptions(contractTypeConfig),
      contractTypeCfg: contractTypeConfig,
      feeRuleOptions: [],
      form: {
        id: undefined,
        contractNo: undefined,
        mchNo: '',
        mchName: '',
        textContractNo: '',
        consignAccountNo: '',
        consignAccountName: '',
        entrustAccountNo: '',
        entrustAccountName: '',
        contractDateStart: undefined,
        contractDateEnd: undefined,
        consignYearLimit: undefined,
        remark: '',
        detailList: [],
        // 详情回填(新增时不展示,编辑/查看时由 loadDetail 回填)
        status: undefined,
        approveRemark: undefined,
        approver: undefined,
        approveTime: undefined,
        syncStatus: undefined,
        syncFailMsg: undefined,
      },
      rules: {
        // 只对原生 a-date-picker 字段保留 form-model 校验
        // 其他字段（代销方/委托方 选商户选钱包）由 save() 手动校验
        contractDateStart: [{ required: true, message: '请选择起始日期', trigger: 'change' }],
        contractDateEnd: [{ required: true, message: '请选择截止日期', trigger: 'change' }],
      },
    }
  },
  methods: {
    statusCfg(v) { return contractStatusConfig[v] || contractStatusConfig.default },
    // 选择代销商户（仅回填商户号/名,不再自动带出代销账号）
    onMchChosen(mch) {
      this.form.mchNo = mch.mchNo
      this.form.mchName = mch.mchName
    },
    // 选择代销钱包（独立选择,与商户解耦）
    onConsignChosen(wallet) {
      this.form.consignAccountNo = wallet.accountNo
      this.form.consignAccountName = wallet.accountName
      this.consignAccountClass = wallet.accountClass
    },
    // 选择委托钱包后,自动回填账号/户名
    onEntrustChosen(wallet) {
      this.form.entrustAccountNo = wallet.accountNo
      this.form.entrustAccountName = wallet.accountName
      this.entrustAccountClass = wallet.accountClass
    },
    // 选择明细的对手方钱包后,自动回填该行 oppAccountNo / oppAccountName / accountClass
    onOppChosen(record, wallet) {
      record.oppAccountNo = wallet.accountNo
      record.oppAccountName = wallet.accountName
      record.oppAccountClass = wallet.accountClass
    },
    // 选择服务费规则后,自动回填 feeName（FeeRule.feeName 快照）
    onFeeRuleChange(record, feeNo) {
      const rule = this.feeRuleOptions.find(r => r.feeNo === feeNo)
      record.feeName = rule ? rule.feeName : ''
    },
    addDetail() {
      this.form.detailList.push({
        contractType: undefined,
        oppAccountNo: '',
        oppAccountName: '',
        oppAccountClass: undefined,
        feeNo: '',
        feeName: '',
        feeRemark: '',
      })
    },
    removeDetail(i) {
      this.form.detailList.splice(i, 1)
    },
    // 加载服务费规则下拉选项
    async loadFeeRules() {
      try {
        const res = await this.api.getCommisionFeeRuleApi({})
        if (res.code === 200) {
          this.feeRuleOptions = res.data || []
        }
      } catch (e) {
        this.$message.error('服务费规则加载失败:' + (e.message || e))
      }
    },
    async save() {
      // form-model 校验（只覆盖日期字段）
      const ok = await new Promise(resolve => {
        this.$refs.ruleForm.validate(valid => resolve(valid))
      })
      if (!ok) return null
      // 手动校验：代销方
      if (!this.form.mchNo) {
        this.$message.error('请选择代销商户')
        return null
      }
      if (!this.form.consignAccountNo) {
        this.$message.error('请填写代销账号')
        return null
      }
      if (!this.form.consignAccountName) {
        this.$message.error('请填写代销户名')
        return null
      }
      // 手动校验：委托方
      if (!this.form.entrustAccountNo) {
        this.$message.error('请选择委托账户')
        return null
      }
      if (!this.form.entrustAccountName) {
        this.$message.error('请填写委托户名')
        return null
      }
      // 手动校验：合同明细
      if (!this.form.detailList || this.form.detailList.length === 0) {
        this.$message.error('请至少添加一条合同明细')
        return null
      }
      for (const d of this.form.detailList) {
        if (!d.contractType || !d.oppAccountNo || !d.oppAccountName
            || !d.feeNo || !d.feeName) {
          this.$message.error('合同明细必填项缺失')
          return null
        }
      }
      const res = await this.api.contractSaveApi(this.form)
      if (res.code !== 200) {
        this.$message.error(res.info || '保存失败')
        return null
      }
      return res.data
    },
    async onSave() {
      this.saveLoading = true
      try {
        const data = await this.save()
        if (data === null) return
        this.$message.success(this.isEdit ? '已保存,合同状态已重置为待审批' : '已保存,合同已进入待审批')
        // 跳回列表(带 fromAddPage / fromEditPage 标记,触发列表 activated 刷新)
        this.$router.replace({
          path: '/consign/contract',
          query: this.isEdit ? { fromEditPage: 'true' } : { fromAddPage: 'true' },
        })
      } finally {
        this.saveLoading = false
      }
    },
    onCancel() {
      // 取消编辑:同样带标记跳回,让列表激活时清空 selected 残留
      this.$router.replace({
        path: '/consign/contract',
        query: this.isEdit ? { fromEditPage: 'true' } : { fromAddPage: 'true' },
      })
    },
    async loadDetail() {
      const contractNo = this.$route.query.contractNo
      if (!contractNo) return
      this.isEdit = true
      this.title = '代销协议-编辑'
      this.loading = true
      try {
        const res = await this.api.contractDetailApi(contractNo)
        if (res.code !== 200) {
          this.$message.error(res.info || '加载失败')
          return
        }
        const data = res.data || {}
        this.form = { ...this.form, ...data, detailList: data.detailList || [] }
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    // 路由 ?type=view → 只读查看
    if (this.$route.query.type === 'view') {
      this.isView = true
      this.title = '代销协议-查看'
    }
    this.loadFeeRules()
    this.loadDetail()
  },
}
</script>

<style lang="less" scoped>
.form-box {
  .toptitle {
    font-weight: 600;
    margin: 10px 0;
  }

  .empty-tip {
    text-align: center; color: #999; padding: 24px;
    border: 1px dashed #d9d9d9; border-radius: 4px; margin-top: 8px;
  }

  .detail-card {
    margin-bottom: 16px;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    transition: box-shadow 0.2s;
    min-width: 280px;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    /deep/ .ant-card-head {
      min-height: 40px;
      padding: 0 14px;
      border-bottom: 1px solid #f0f0f0;
    }

    /deep/ .ant-card-head-title {
      padding: 8px 0;
      font-size: 13px;
    }

    /deep/ .ant-card-extra {
      padding: 6px 0;
    }

    /deep/ .ant-card-body {
      padding: 18px 16px;
    }
  }

  .detail-field {
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    min-height: 32px;

    &:last-child {
      margin-bottom: 0;
    }

    label {
      width: 90px;
      color: #666;
      font-size: 13px;
      text-align: right;
      padding-right: 8px;
      flex-shrink: 0;
      white-space: nowrap;
    }

    label.required::before {
      content: '*';
      color: #f5222d;
      margin-right: 4px;
    }

    > .ant-select,
    > .ant-input,
    > .ant-input-number,
    > span {
      flex: 1;
      min-width: 0;
    }

    .placeholder {
      color: #bbb;
      font-size: 12px;
    }
  }

  /deep/ .ant-form-item {
    display: flex;
    margin-bottom: 0;

    .mch-style {
      .ant-form-item-control-wrapper {
        border: none;
        padding: 5px 0 0 0;
        margin-right: 15px;
      }
    }

    .ant-form-item-label {
      width: 140px;
      background-color: rgba(242, 242, 242, 1);
      padding-left: 20px;
      display: flex;
      align-items: center;
    }

    .ant-form-item-control-wrapper {
      flex: 1;
      border: 1px solid rgba(242, 242, 242, 1);
      padding: 5px 40px;

      .ant-form-item-children {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
      }

      .ant-input,
      .el-input__inner {
        max-width: 300px;
      }

      .ant-select {
        width: 200px;
      }

      textarea {
        resize: none;
      }
    }
  }
}
</style>
